<template>
  <div
    v-if="initiallyCalculated && !isInPast"
    v-editable="blok"
    class="flex items-center space-x-2 rounded-full border-DEFAULT border-[#2e3033] px-4 py-2 text-sm font-medium tabular-nums"
  >
    <div>
      {{
        $tc('countdown.days', countdown.days, { days: padTime(countdown.days) })
      }}
    </div>
    <div>·</div>
    <div>
      {{
        $tc('countdown.hours', countdown.hours, {
          hours: padTime(countdown.hours),
        })
      }}
    </div>
    <div>·</div>
    <div>
      {{
        $tc('countdown.minutes', countdown.minutes, {
          minutes: padTime(countdown.minutes),
        })
      }}
    </div>
    <div>·</div>
    <div>
      {{
        $tc('countdown.seconds', countdown.seconds, {
          seconds: padTime(countdown.seconds),
        })
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Countdown as SbCountdown } from '~/@types/storyblok'
const props = defineProps({
  blok: {
    type: Object as PropType<SbCountdown>,
    required: true,
  },
})
interface Countdown {
  total: number
  seconds: number
  minutes: number
  hours: number
  days: number
}
const state = reactive<Countdown>({
  total: 0,
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
})
const initiallyCalculated = ref(false)
const getRemainingTime = (until: Date): Countdown => {
  const currentDate = new Date()
  const total =
    Date.parse(until.toString()) - Date.parse(currentDate.toString())
  const seconds = Math.floor((total / 1000) % 60)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  return {
    total,
    seconds,
    minutes,
    hours,
    days,
  }
}
const setState = (countdown: Countdown) => {
  const { days, hours, seconds, minutes, total } = countdown

  if (total < 0) {
    return
  }
  state.total = total
  state.days = days
  state.hours = hours
  state.minutes = minutes
  state.seconds = seconds
}

// Storyblok does not return ISO 8601 strings for the Date/Time Blok type
// The Date constructor is only required to work with ISO 8601 dates.
// Any other formats might not be supported depending on the browser
// Replacing the space with T makes it compliant...
const untilDate = new Date(props.blok.countdownUntil.replace(' ', 'T'))

onBeforeMount(() => {
  setState(getRemainingTime(untilDate))
  initiallyCalculated.value = true
})
onMounted(() => {
  const countdownInterval = setInterval(() => {
    const remaining = getRemainingTime(untilDate)
    setState(remaining)
    if (remaining.total === 0) {
      clearInterval(countdownInterval)
    }
  }, 1000)
})

const isInPast = computed(() => toRaw(state).total <= 0)

const padTime = (time: number) => `${time}`.padStart(2, '0')

const countdown = state
</script>
